import { useState, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import { gameSettingsAtom, animatedBeatIdsAtom } from '../atoms/playerAtoms'

export function useTypewriter(text, { enabled = true, beatId = null } = {}) {
  const [settings] = useAtom(gameSettingsAtom)
  const [animatedBeatIds, setAnimatedBeatIds] = useAtom(animatedBeatIdsAtom)
  const [displayed, setDisplayed] = useState('')
  const [isDone, setIsDone] = useState(false)
  const intervalRef = useRef(null)

  // Skip animation if this beat was already animated this session
  const alreadyAnimated = beatId && animatedBeatIds.has(beatId)

  useEffect(() => {
    if (!enabled || !text || alreadyAnimated) {
      setDisplayed(text || '')
      setIsDone(true)
      return
    }

    setDisplayed('')
    setIsDone(false)
    let i = 0

    // Use a fixed tick rate and vary characters-per-tick for speed.
    // setInterval is clamped to ~4ms by browsers, so lowering the
    // interval below that has no effect. Instead we tick at ~30fps
    // and advance more characters per tick for faster speeds.
    const TICK_MS = 32
    const charsPerTick = Math.max(1, Math.round(TICK_MS / settings.textSpeed))

    intervalRef.current = setInterval(() => {
      i += charsPerTick
      if (i >= text.length) {
        setDisplayed(text)
        setIsDone(true)
        clearInterval(intervalRef.current)
        // Mark this beat as animated
        if (beatId) {
          setAnimatedBeatIds((prev) => new Set(prev).add(beatId))
        }
      } else {
        setDisplayed(text.slice(0, i))
      }
    }, TICK_MS)

    return () => clearInterval(intervalRef.current)
  }, [text, enabled, settings.textSpeed, alreadyAnimated])

  const skip = () => {
    clearInterval(intervalRef.current)
    setDisplayed(text || '')
    setIsDone(true)
    if (beatId) {
      setAnimatedBeatIds((prev) => new Set(prev).add(beatId))
    }
  }

  return { displayed, isDone, skip }
}
